import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getCallHistory, deleteCall, formatCallDuration, formatCallStatus } from "../../functions/call";
import { useCall } from "../../components/call";
import Header from "../../components/header";
import { PhoneIcon, VideoIcon, DeleteIcon } from "../../svg/chat";
import "./style.css";

export default function CallHistory() {
  const { user } = useSelector((state) => state.user);
  const { initiateCall } = useCall();
  const navigate = useNavigate();

  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState("all"); // all, missed, video, voice

  // Load call history
  const loadCalls = useCallback(async (pageNum = 1, append = false) => {
    setLoading(true);
    const result = await getCallHistory(pageNum, 20, user.token);

    if (result.status === "ok") {
      if (append) {
        setCalls((prev) => [...prev, ...result.calls]);
      } else {
        setCalls(result.calls);
      }
      setHasMore(pageNum < result.pagination.pages);
    }
    setLoading(false);
  }, [user.token]);

  useEffect(() => {
    loadCalls();
  }, [loadCalls]);

  // Load more
  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadCalls(nextPage, true);
  };

  // Delete call
  const handleDelete = async (callId) => {
    if (window.confirm("Remover esta chamada do historico?")) {
      const result = await deleteCall(callId, user.token);
      if (result.status === "ok") {
        setCalls((prev) => prev.filter((c) => c._id !== callId));
      }
    }
  };

  // Call back
  const handleCallBack = (call) => {
    // Find the other user
    const otherUser = call.caller._id === user.id
      ? call.participants.find((p) => p.user._id !== user.id)?.user
      : call.caller;

    if (otherUser) {
      initiateCall(otherUser._id, call.conversationId, call.type);
    }
  };

  // Filter calls
  const filteredCalls = calls.filter((call) => {
    if (filter === "all") return true;
    if (filter === "missed") {
      return call.status === "missed" &&
        call.participants.find((p) => p.user._id === user.id)?.status === "missed";
    }
    if (filter === "video") return call.type === "video";
    if (filter === "voice") return call.type === "voice";
    return true;
  });

  // Group calls by date
  const groupedCalls = filteredCalls.reduce((groups, call) => {
    const date = new Date(call.createdAt).toLocaleDateString("pt-BR", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(call);
    return groups;
  }, {});

  // Get call direction and status
  const getCallInfo = (call) => {
    const isCaller = call.caller._id === user.id;
    const myParticipant = call.participants.find((p) => p.user._id === user.id);
    const otherUser = isCaller
      ? call.participants.find((p) => p.user._id !== user.id)?.user
      : call.caller;

    let direction = isCaller ? "outgoing" : "incoming";
    let status = myParticipant?.status || call.status;

    return { isCaller, otherUser, direction, status };
  };

  return (
    <div className="call_history_page">
      <Header page="calls" />

      <div className="call_history_container">
        <div className="call_history_header">
          <h1>Historico de Chamadas</h1>
          <button
            className="new_call_btn"
            onClick={() => navigate("/conference")}
          >
            Nova conferencia
          </button>
        </div>

        {/* Filters */}
        <div className="call_filters">
          <button
            className={`filter_btn ${filter === "all" ? "active" : ""}`}
            onClick={() => setFilter("all")}
          >
            Todas
          </button>
          <button
            className={`filter_btn ${filter === "missed" ? "active" : ""}`}
            onClick={() => setFilter("missed")}
          >
            Perdidas
          </button>
          <button
            className={`filter_btn ${filter === "video" ? "active" : ""}`}
            onClick={() => setFilter("video")}
          >
            Video
          </button>
          <button
            className={`filter_btn ${filter === "voice" ? "active" : ""}`}
            onClick={() => setFilter("voice")}
          >
            Voz
          </button>
        </div>

        {/* Call list */}
        <div className="call_list">
          {loading && calls.length === 0 ? (
            <div className="loading_state">
              <div className="spinner"></div>
              <p>Carregando...</p>
            </div>
          ) : filteredCalls.length === 0 ? (
            <div className="empty_state">
              <PhoneIcon color="#65676b" />
              <h3>Nenhuma chamada</h3>
              <p>Seu historico de chamadas aparecera aqui</p>
            </div>
          ) : (
            Object.entries(groupedCalls).map(([date, dayCalls]) => (
              <div key={date} className="call_group">
                <div className="call_group_header">{date}</div>
                {dayCalls.map((call) => {
                  const { isCaller, otherUser, direction, status } = getCallInfo(call);
                  const isMissed = status === "missed" || status === "rejected";

                  return (
                    <div key={call._id} className="call_item">
                      <div className="call_avatar">
                        <img
                          src={otherUser?.picture || "/images/default_user.png"}
                          alt=""
                        />
                      </div>

                      <div className="call_info">
                        <span className={`call_name ${isMissed ? "missed" : ""}`}>
                          {otherUser?.first_name} {otherUser?.last_name}
                        </span>
                        <span className="call_details">
                          <span className={`call_direction ${direction} ${isMissed ? "missed" : ""}`}>
                            {direction === "outgoing" ? (
                              <svg viewBox="0 0 16 16" width="14" height="14">
                                <path fill="currentColor" d="M11 5.5l-5 5 1.5 1.5L13 6.5V11h2V3h-8v2h4.5z" />
                              </svg>
                            ) : (
                              <svg viewBox="0 0 16 16" width="14" height="14">
                                <path fill="currentColor" d="M5 10.5l5-5L8.5 4 3 9.5V5H1v8h8v-2H4.5z" />
                              </svg>
                            )}
                          </span>
                          {formatCallStatus(status, call.type, isCaller)}
                          {call.duration > 0 && (
                            <span className="call_duration">
                              {" "}• {formatCallDuration(call.duration)}
                            </span>
                          )}
                        </span>
                      </div>

                      <div className="call_time">
                        {new Date(call.createdAt).toLocaleTimeString("pt-BR", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>

                      <div className="call_actions">
                        <button
                          className="call_action_btn"
                          onClick={() => handleCallBack(call)}
                          title={call.type === "video" ? "Chamada de video" : "Chamada de voz"}
                        >
                          {call.type === "video" ? (
                            <VideoIcon color="#25d366" />
                          ) : (
                            <PhoneIcon color="#25d366" />
                          )}
                        </button>
                        <button
                          className="call_action_btn delete"
                          onClick={() => handleDelete(call._id)}
                          title="Remover"
                        >
                          <DeleteIcon color="#e74c3c" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}

          {hasMore && !loading && (
            <button className="load_more_btn" onClick={loadMore}>
              Carregar mais
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
